import { Box, ButtonBase, SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import {
  DEFAULT_BLUE_TEXT_COLOR,
  DEFAULT_SECONDARY_TEXT_COLOR,
} from "../utils/utils";
import Flex from "./Flex";
import Text from "./Text";
import { CHART_DIMENSIONS } from "./app/provider/patient/chart";

export interface ToggleTabItem<T extends string = string> {
  label?: string;
  value: T;
  icon?: ReactNode;
}

interface ToggleTabsProps<T extends string = string> {
  tabs: ToggleTabItem<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  rightActions?: ReactNode;
  containerSx?: SxProps<Theme>;
}

const ToggleTabs = <T extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  rightActions,
  containerSx,
}: ToggleTabsProps<T>) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      px={1.5}
      py={1}
      sx={{
        borderBottom: "1px solid #E7E7E7",
        backgroundColor: "#FFFFFF",
        ...containerSx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#F5F5F5",
          borderRadius: "8px",
          // padding: "3px",
          gap: "4px",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <ButtonBase
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              sx={{
                px: 2,
                height: CHART_DIMENSIONS.controlHeight,
                borderRadius: "6px",
                backgroundColor: isActive ? "#FFFFFF" : "transparent",
                boxShadow: isActive
                  ? "0px 1px 3px rgba(0, 0, 0, 0.12)"
                  : "none",
                border: isActive
                  ? "1px solid #E0E0E0"
                  : "1px solid transparent",
                transition: "all 0.2s ease",
                gap: "6px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {tab.icon && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: isActive
                      ? DEFAULT_BLUE_TEXT_COLOR
                      : DEFAULT_SECONDARY_TEXT_COLOR,
                  }}
                >
                  {tab.icon}
                </Box>
              )}
              {tab.label && (
                <Text
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: "14px",
                    lineHeight: isActive ? "120%" : "160%",
                    color: isActive
                      ? DEFAULT_BLUE_TEXT_COLOR
                      : DEFAULT_SECONDARY_TEXT_COLOR,
                  }}
                >
                  {tab.label}
                </Text>
              )}
            </ButtonBase>
          );
        })}
      </Box>
      {rightActions && <Flex gap={1}>{rightActions}</Flex>}
    </Flex>
  );
};

export default ToggleTabs;
